import { useRef, useState, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { AccountingEngine } from '../services/accountingEngine';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { formatDate } from '../lib/dateUtils';

export const StockSummaryView = ({ config, handleItemSelect, selectedIndex, setSelectedIndex, activeMenuLevel }: any) => {
  const scrollRef = useRef(null);
  const activeCompany = useStore((state) => state.activeCompany);
  const toDate = useStore((state) => state.toDate);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const isPharmacy = (activeCompany as any)?.businessNature === 'pharmacy';

  const stockGroupId = activeMenuLevel?.startsWith('display_stocksummary_')
    ? Number(activeMenuLevel.split('_').pop())
    : null;

  const stockData = useLiveQuery(async () => {
    if (!activeCompany) return { items: [], totalValue: 0 };
    return await AccountingEngine.generateStockSummary(activeCompany.id!, toDate, stockGroupId);
  }, [activeCompany, toDate, stockGroupId]);

  // Pharmacy: fetch extra fields (mrp, pack, rackLocation)
  const stockItems = useLiveQuery(async () => {
    if (!activeCompany) return [];
    return db.stockItems.where('companyId').equals(activeCompany.id!).toArray();
  }, [activeCompany]);

  const stockItemMap = useMemo(() => {
    if (!stockItems) return new Map();
    return new Map(stockItems.map(s => [s.name, s]));
  }, [stockItems]);

  const flatItems = useMemo(() => {
    const items = stockData?.items || [];
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((item: any) =>
      item.name?.toLowerCase().includes(q) ||
      (stockItemMap.get(item.name) as any)?.manufacturer?.toLowerCase().includes(q) ||
      (stockItemMap.get(item.name) as any)?.saltComposition?.toLowerCase().includes(q)
    );
  }, [stockData, search, stockItemMap]);

  useWheelNavigation(scrollRef, flatItems.length, setSelectedIndex, false, () => flatItems && flatItems[selectedIndex] && handleItemSelect(flatItems[selectedIndex], selectedIndex));

  const totalValue = flatItems.reduce((s: number, it: any) => s + (it.value || 0), 0);
  const totalQty = flatItems.reduce((s: number, it: any) => s + (it.isGroup ? 0 : (it.quantity || 0)), 0);
  const itemCount = flatItems.filter((it: any) => !it.isGroup).length;

  if (!stockData) {
    return (
      <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-black justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e8585]"></div>
        <div className="mt-4 font-bold text-gray-500">Loading Stock Summary...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      {/* Header */}
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px]">
        <span className="w-1/3 text-left">Stock Summary</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      {/* Stats Bar + Search */}
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-[#aebec8] bg-white shadow-sm shrink-0">
        <div className="flex items-center gap-4">
          <span className="text-[11px] text-slate-500">As on: <strong className="text-slate-700">{formatDate(toDate)}</strong></span>
          <div className="h-3 w-px bg-slate-300" />
          <span className="text-[10px] font-semibold text-[#1e8585]">{itemCount} Items</span>
          <span className="text-[10px] font-semibold text-emerald-600">Qty: {totalQty.toFixed(0)}</span>
          <span className="text-[10px] font-semibold text-blue-600">Value: ₹{totalValue.toFixed(2)}</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={searchRef}
            type="text" value={search}
            onChange={e => { setSearch(e.target.value); setSelectedIndex(0); }}
            placeholder={isPharmacy ? "Search medicine, salt, manufacturer..." : "Search item..."}
            className="border border-gray-300 rounded px-2 py-[3px] text-[11px] w-[220px] outline-none focus:border-[#1e8585] focus:ring-1 focus:ring-[#1e8585]/20"
            onKeyDown={e => { if (e.key === 'Escape') { setSearch(''); searchRef.current?.blur(); } }}
          />
          {search && (
            <span className="text-[10px] text-amber-600 font-semibold">{flatItems.length} found</span>
          )}
        </div>
      </div>

      {/* Column Headers */}
      <div className="flex border-b-2 border-[#aebec8] font-bold px-2 py-1 bg-gray-50 shrink-0 text-[11px]">
        <div className={isPharmacy ? "w-[30%]" : "w-[40%]"}>Item Name</div>
        {isPharmacy && <div className="w-[12%] text-center">Pack</div>}
        {isPharmacy && <div className="w-[12%] text-center">Rack</div>}
        <div className="w-[12%] text-right">Quantity</div>
        <div className="w-[12%] text-right">Rate</div>
        <div className="w-[12%] text-right">Value</div>
        {isPharmacy && <div className="w-[10%] text-right">MRP</div>}
      </div>

      {/* List */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {flatItems.length === 0 ? (
          <div className="text-center text-gray-400 py-12 text-[12px]">
            {search ? 'No matching items found' : 'No stock items'}
          </div>
        ) : (
          flatItems.map((item: any, idx: number) => {
            const si = stockItemMap.get(item.name);
            const isLow = item.quantity > 0 && (si as any)?.reorderLevel && item.quantity <= (si as any).reorderLevel;
            const isZero = !item.isGroup && item.quantity <= 0;
            const rate = item.quantity > 0 ? (item.value / item.quantity) : 0;

            return (
              <div
                key={idx}
                className={`flex px-2 py-[3px] cursor-pointer border-b border-gray-50 ${
                  selectedIndex === idx ? 'bg-[#1e8585] text-white font-bold active-row'
                  : isZero ? 'bg-rose-50/50 text-rose-400'
                  : isLow ? 'bg-amber-50/50'
                  : 'text-black hover:bg-[#f0f9f9]'
                }`}
                onClick={() => handleItemSelect(item, idx)}
                onMouseEnter={() => setSelectedIndex(idx)}
              >
                <div className={`${isPharmacy ? "w-[30%]" : "w-[40%]"} truncate ${item.isGroup ? 'font-bold' : 'pl-2'}`}>
                  {item.name}
                  {isLow && selectedIndex !== idx && <span className="ml-1 text-[9px] text-amber-600 font-bold">LOW</span>}
                  {isZero && selectedIndex !== idx && <span className="ml-1 text-[9px] text-rose-400 font-bold">OUT</span>}
                </div>
                {isPharmacy && (
                  <div className={`w-[12%] text-center text-[11px] ${selectedIndex === idx ? 'text-white/70' : 'text-gray-400'}`}>
                    {(si as any)?.pack || ''}
                  </div>
                )}
                {isPharmacy && (
                  <div className={`w-[12%] text-center text-[11px] ${selectedIndex === idx ? 'text-white/70' : 'text-gray-400'}`}>
                    {(si as any)?.rackLocation || ''}
                  </div>
                )}
                <div className={`w-[12%] text-right font-semibold ${isZero && selectedIndex !== idx ? 'text-rose-400' : ''}`}>
                  {item.isGroup ? '' : `${item.quantity} ${item.unit || ''}`}
                </div>
                <div className="w-[12%] text-right">
                  {item.isGroup ? '' : rate > 0 ? rate.toFixed(2) : ''}
                </div>
                <div className={`w-[12%] text-right font-semibold`}>
                  {item.isGroup ? '' : item.value > 0 ? item.value.toFixed(2) : ''}
                </div>
                {isPharmacy && (
                  <div className={`w-[10%] text-right ${selectedIndex === idx ? 'text-white/70' : 'text-gray-500'}`}>
                    {(si as any)?.mrp ? `₹${(si as any).mrp}` : ''}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer Total */}
      <div className="flex border-t-2 border-[#aebec8] font-bold px-2 py-1 bg-gray-50 shrink-0">
        <div className={isPharmacy ? "w-[30%]" : "w-[40%]"}>Total ({itemCount})</div>
        {isPharmacy && <div className="w-[12%]" />}
        {isPharmacy && <div className="w-[12%]" />}
        <div className="w-[12%] text-right text-[#1e8585]">{totalQty.toFixed(0)}</div>
        <div className="w-[12%]" />
        <div className="w-[12%] text-right text-[#1e8585]">{totalValue.toFixed(2)}</div>
        {isPharmacy && <div className="w-[10%]" />}
      </div>
    </div>
  );
};
